import { call, put, SagaGenerator, select } from "typed-redux-saga";
import { produce } from "immer";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { createCustomNotificationAction } from "../../../app/actions";
import { defaultLocalizer, localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { extractCause, isRawHttpStatusCause } from "../../../utilities/error";

import { isArchiveModalDocumentData } from "../archiveModalTypes";
import { isXakteArchiveResult, xakteStatusRequest, xakteSubmitRequest, XakteArchiveResult } from "../xakteApi";

const logger = LoggerFactory.getLogger("xakteArchiveSaga");
const POLLING_INTERVAL_MS = 5000;

export function* submitXakteArchive(
	activityId: string,
	sourceActivityId: string,
	previewId: string
): SagaGenerator<boolean> {
	yield* call(setXakteArchiveBusy, activityId, true);

	let result: XakteArchiveResult | undefined;
	let failed = false;
	yield* call(
		fetchSagaFactory(
			xakteSubmitRequest(previewId),
			function* handleResponse(response) {
				const responseBody: unknown = yield* call(response.json.bind(response));
				if (!isXakteArchiveResult(responseBody)) {
					throw new Error("Response is not a valid xAkte archive result.");
				}
				result = responseBody;
			},
			function* handleError(error: unknown) {
				failed = true;
				yield* call(handleXakteArchiveError, activityId, error);
			}
		)
	);

	if (failed || !result) {
		return false;
	}
	if (result.status === "PENDING") {
		result = yield* call(pollXakteArchive, activityId, previewId);
	}
	if (!result || result.status !== "SUCCEEDED") {
		if (result?.status === "FAILED") {
			yield* call(
				handleXakteArchiveError,
				activityId,
				new Error(result.errorMessage ?? result.errorCode ?? "Die xAkte-Veraktung ist fehlgeschlagen.")
			);
		}
		return false;
	}

	const sourceActivity = yield* select(ActivitySelectors.activityById(sourceActivityId));
	if (sourceActivity) {
		yield* call(reloadTaskAndAntragActivity);
	}
	yield* call(setXakteArchiveBusy, activityId, false);
	yield* put(ActivityActions.cancel({ activityId }));
	return true;
}

export function* pollXakteArchive(
	activityId: string,
	previewId: string
): SagaGenerator<XakteArchiveResult | undefined> {
	let result: XakteArchiveResult | undefined;
	yield* call(
		fetchSagaFactory(
			xakteStatusRequest(previewId),
			function* handleResponse(response) {
				const responseBody: unknown = yield* call(response.json.bind(response));
				if (!isXakteArchiveResult(responseBody)) {
					throw new Error("Response is not a valid xAkte archive result.");
				}
				result = responseBody;
				if (responseBody.status !== "PENDING") {
					return undefined;
				}
				const activity = yield* select(ActivitySelectors.activityById(activityId));
				if (!activity) {
					result = undefined;
					return undefined;
				}
				return POLLING_INTERVAL_MS;
			},
			function* handleError(error: unknown) {
				yield* call(handleXakteArchiveError, activityId, error);
			}
		)
	);
	return result;
}

export function* setXakteArchiveBusy(activityId: string, busy: boolean): SagaGenerator<void> {
	const data = yield* select(ActivitySelectors.data(activityId));
	if (!isArchiveModalDocumentData(data)) {
		return;
	}
	yield* put(
		ActivityActions.setData({
			activityId,
			data: produce(data, draft => {
				if (!draft.document.root.metadata) {
					draft.document.root.metadata = {};
				}
				draft.document.root.metadata.busy = busy;
			})
		})
	);
}

export function* handleXakteArchiveError(activityId: string, error: unknown): SagaGenerator<void> {
	logger.error("xAkte archiving failed:", error);
	const cause = extractCause(error);
	const localizableMessage =
		cause && !isRawHttpStatusCause(cause)
			? localizableFromLocaleResources(
					RESOURCE_KEYS.features.notification.error.xakte.transmittingErrorWithCause,
					{
						cause: { type: "plain", value: cause }
					}
				)
			: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.error.xakte.transmittingError);
	const message = (defaultLocalizer(localizableMessage) ?? "").trim();
	const data = yield* select(ActivitySelectors.data(activityId));
	if (isArchiveModalDocumentData(data)) {
		yield* put(
			ActivityActions.setData({
				activityId,
				data: produce(data, draft => {
					if (!draft.document.root.metadata) {
						draft.document.root.metadata = {};
					}
					draft.document.root.metadata.status = "error";
					draft.document.root.metadata.message = message;
					draft.document.root.metadata.busy = false;
					draft.document.root.metadata.cancelDisabled = false;
				})
			})
		);
	}
	yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(false) }));
	yield* put(
		createCustomNotificationAction({
			title: { key: RESOURCE_KEYS.features.notification.title.error },
			message: localizableMessage,
			severity: "error",
			activityId,
			group: `xakteArchiving:${activityId}`
		})
	);
}
